import axios from 'axios';
import { formatDateTime } from '../../utils/dates';

// Agents vocaux — the voice section, separate from the text agents: voice
// agents (/voice-agents), numbers (/voice-numbers), calls (/voice-tickets).
// Mirrors Backend app/api/voice_agents.py, app/api/voice_numbers.py (buy /
// route / cancel a number) and app/api/ai_voice.py (tool catalogue, browser
// preview).

export const API = '/api/voice-numbers';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const enc = (number) => encodeURIComponent(number);

export const getNumbers = () => axios.get(API, auth());
export const startCheckout = (body) => axios.post(`${API}/checkout`, body, auth());
export const confirmCheckout = (sessionId) => axios.post(`${API}/checkout/confirm`, { session_id: sessionId }, auth());
export const setAgent = (number, agentId) => axios.put(`${API}/${enc(number)}/agent`, { agent_id: agentId || null }, auth());
export const cancelNumber = (number) => axios.post(`${API}/${enc(number)}/cancel`, {}, auth());
export const resumeNumber = (number) => axios.post(`${API}/${enc(number)}/resume`, {}, auth());
export const removeNumber = (number) => axios.delete(`${API}/${enc(number)}`, auth());
export const getInvoices = () => axios.get(`${API}/invoices`, auth());

export const AGENTS_API = '/api/voice-agents';
export const listAgents = () => axios.get(AGENTS_API, auth());
export const createAgent = (body) => axios.post(AGENTS_API, body, auth());
export const getAgent = (agentId) => axios.get(`${AGENTS_API}/${agentId}`, auth());
export const updateAgent = (agentId, body) => axios.put(`${AGENTS_API}/${agentId}`, body, auth());
export const deleteAgent = (agentId) => axios.delete(`${AGENTS_API}/${agentId}`, auth());

export const getVoiceTools = () => axios.get('/api/ai-voice/voice-tools', auth());
export const startPreview = (agentId) => axios.post('/api/ai-voice/demo/session', { assistant_id: agentId }, auth());
export const previewToolCall = (body) => axios.post('/api/ai-voice/demo/tool-call', body, auth());

export const fail = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (detail && typeof detail.message === 'string') return detail.message;
    return fallback;
};

const locale = (lang) => (lang === 'en' ? 'en-GB' : 'fr-FR');
export const formatDay = (value, lang) => (value
    ? formatDateTime(value, { hour: undefined, minute: undefined, day: 'numeric', month: 'long', year: 'numeric' }, locale(lang))
    : '');
export const formatWhen = (value, lang) => (value ? formatDateTime(value, {}, locale(lang)) : '');
export const formatPrice = (eur, lang) => new Intl.NumberFormat(locale(lang), { style: 'currency', currency: 'EUR', maximumFractionDigits: eur % 1 ? 2 : 0 }).format(eur || 0);

// Team members read the page; only account admins buy, route and cancel.
export const canManage = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || '{}').role !== 'employee';
    } catch (e) {
        return true;
    }
};

export const REALTIME_VOICES = ['marin', 'cedar', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'];
export const DEFAULT_VOICE = 'marin';
export const SPEEDS = ['0.9', '1', '1.05', '1.15', '1.25'];
export const LANGUAGES = ['fr', 'en', 'es', 'de', 'it'];
export const TOOL_GROUPS = ['calls', 'parcels', 'support', 'integration', 'restaurant', 'owner', 'health', 'doctor', 'other'];

// French defaults of the labels whose key is built from data (the locale
// files carry the translations).
export const GROUP_LABELS = {
    calls: 'Appel', parcels: 'Colis', support: 'Support', integration: 'Intégrations', restaurant: 'Restaurant',
    owner: 'Gérant', health: 'Santé', doctor: 'Médecin', other: 'Autres', unavailable: 'Non proposés à ce compte',
};
export const LANGUAGE_LABELS = { fr: 'Français', en: 'Anglais', es: 'Espagnol', de: 'Allemand', it: 'Italien' };
export const SPEED_LABELS = { '0.9': 'Posé (0,9)', 1: 'Normal (1)', '1.05': 'Standard (1,05)', '1.15': 'Rapide (1,15)', '1.25': 'Très rapide (1,25)' };
export const INVOICE_STATUS_LABELS = { paid: 'Payée', open: 'À régler', draft: 'Brouillon', void: 'Annulée', uncollectible: 'Impayée' };

export const TEMPLATES = ['parcel_support', 'reception', 'blank'];

export const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const s = Math.round(seconds);
    return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
};
